import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';

// Color utility functions
const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
};

const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// Generate theme colors from a base color
const generateThemeColors = (baseColor, isDark = true) => {
  const [h, s, l] = hexToHsl(baseColor);
  
  if (isDark) {
    // Dark theme (current)
    return {
      primary: baseColor,
      secondary: hslToHex(h, Math.max(s - 10, 0), Math.min(l + 10, 90)),
      accent: hslToHex(h, Math.max(s - 20, 0), Math.min(l + 20, 95)),
      background: {
        dark: hslToHex(h, Math.min(s + 20, 100), Math.max(l - 45, 5)),
        main: hslToHex(h, Math.min(s + 15, 100), Math.max(l - 35, 10)),
        light: hslToHex(h, Math.min(s + 10, 100), Math.max(l - 25, 15)),
        card: hslToHex(h, Math.min(s + 12, 100), Math.max(l - 30, 12)),
      },
      text: {
        primary: '#FFFFFF',
        secondary: hslToHex(h, Math.max(s - 30, 20), Math.min(l + 35, 85)),
        muted: hslToHex(h, Math.max(s - 20, 30), Math.min(l + 25, 75)),
      },
      border: hslToHex(h, Math.min(s + 5, 100), Math.max(l - 20, 20)),
    };
  } else {
    // Light theme - completely redesigned
    return {
      primary: hslToHex(h, Math.min(s + 10, 100), Math.max(l - 15, 25)), // Darker, more saturated primary
      secondary: hslToHex(h, Math.min(s + 5, 100), Math.max(l - 10, 30)), // Slightly darker secondary
      accent: hslToHex(h, Math.min(s + 15, 100), Math.max(l - 5, 35)), // Bright accent
      background: {
        dark: '#FFFFFF', // Pure white background
        main: hslToHex(h, Math.max(s - 50, 5), Math.min(l + 50, 98)), // Very light tinted background
        light: hslToHex(h, Math.max(s - 45, 10), Math.min(l + 45, 96)), // Light card backgrounds
        card: hslToHex(h, Math.max(s - 40, 15), Math.min(l + 40, 94)), // Card background
      },
      text: {
        primary: '#1A1A1A', // Dark text for readability
        secondary: hslToHex(h, Math.min(s + 20, 80), Math.max(l - 30, 20)), // Colored secondary text
        muted: hslToHex(h, Math.min(s + 10, 60), Math.max(l - 20, 30)), // Muted colored text
      },
      border: hslToHex(h, Math.max(s - 20, 20), Math.min(l + 20, 80)), // Light borders with color tint
    };
  }
};

// Helper function to convert hex to rgba
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Base theme structure (non-color properties)
const baseTheme = {
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    xxl: '2rem',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    xl: '2rem',
    circle: '50%',
  },
  shadows: {
    sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
    md: '0 4px 8px rgba(0, 0, 0, 0.3)',
    lg: '0 8px 16px rgba(0, 0, 0, 0.3)',
    glow: '0 0 8px',
  },
  transitions: {
    fast: '0.2s ease',
    medium: '0.3s ease',
    slow: '0.5s ease',
  },
  breakpoints: {
    xs: '400px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
};

// Status colors (remain constant)
const statusColors = {
  success: '#00F5D4',
  warning: '#FFD166',
  error: '#FF5E5B',
  info: '#38B6FF',
};

// Default theme colors
const defaultMainColor = '#7B2CBF';

// Theme context
const DynamicThemeContext = createContext();

export const useDynamicTheme = () => {
  const context = useContext(DynamicThemeContext);
  if (!context) {
    throw new Error('useDynamicTheme must be used within a DynamicThemeProvider');
  }
  return context;
};

export const DynamicThemeProvider = ({ children }) => {
  const [mainColor, setMainColor] = useState(() => {
    // Load saved color from localStorage
    return localStorage.getItem('dex-bot-main-color') || defaultMainColor;
  });

  const [isDark, setIsDark] = useState(() => {
    // Load saved theme mode from localStorage
    return localStorage.getItem('dex-bot-theme-mode') !== 'light';
  });

  // Save color to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dex-bot-main-color', mainColor);
  }, [mainColor]);

  // Save theme mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dex-bot-theme-mode', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Generate complete theme
  const colors = generateThemeColors(mainColor, isDark);
  const theme = {
    ...baseTheme,
    colors: {
      ...colors,
      status: statusColors,
      shadow: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)',
      // Helper functions for dynamic colors
      primaryAlpha: (alpha) => hexToRgba(colors.primary, alpha),
      secondaryAlpha: (alpha) => hexToRgba(colors.secondary, alpha),
      accentAlpha: (alpha) => hexToRgba(colors.accent, alpha),
    },
    isDark,
  };

  const updateMainColor = (newColor) => {
    setMainColor(newColor);
  };

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const resetToDefault = () => {
    setMainColor(defaultMainColor);
    setIsDark(true);
  };

  const contextValue = {
    mainColor,
    isDark,
    updateMainColor,
    toggleTheme,
    resetToDefault,
    theme,
  };

  return (
    <DynamicThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </DynamicThemeContext.Provider>
  );
}; 